import { EMessengerChatSourceType, EMessengerEventBusEvents, MessengerEventBus } from '@xometry/ui';
import { amplitudeLogger } from 'amplitude';
import { Form } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { AmplitudeMessengerEvent } from 'collections/amplitudeEvents';
import { UserLocaleEnum } from 'collections/users';
import { AppContext } from 'contexts/AppContext';
import { UserLocStore } from 'interfaces';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useMessengerSourceRelatedObjectsQuery } from 'utils/graphql/queries/__generated__/messengerSourceRelatedObjects';
import { useUserLocaleQuery } from 'utils/graphql/queries/__generated__/userLocale';
import { TranslateRequest, useAiTranslator } from 'utils/hooks/useAiTranslator';
import { useMessengerEventBus } from 'utils/hooks/useMessengerEventBus';
import useModal from 'utils/hooks/useModal';

import { AiWriterModalFormFieldEnum, AiWriterModalFormValues } from './AiWriterModal.types';
import { getSafeLocale } from './utils';

export interface HandleOpenModalArgs {
  messageText: string;
  sourceId: string;
  sourceType: EMessengerChatSourceType;
  isVisibleToClient: boolean;
  isVisibleToPartner: boolean;
}

const translationCache = new Map<string, string>();

export const useAiWriterModal = () => {
  const [form] = useForm<AiWriterModalFormValues>();
  const { messengerEventBus } = useContext(AppContext);
  const { sendEvent } = useMessengerEventBus();
  const { modalVisible, openModal, closeModal } = useModal();

  // Tracks which action was last executed: rewrite or proofread
  const [lastAction, setLastAction] = useState<null | TranslateRequest>(null);

  // Whether the Proof-reading collapse section is currently open
  const [isProofOpen, setIsProofOpen] = useState(false);

  // Whether the user manually edited the ImprovedMessage field
  const [isUserEdited, setIsUserEdited] = useState(false);

  const [source, setSource] = useState<Omit<HandleOpenModalArgs, 'messageText'>>();

  const { data: relatedData } = useMessengerSourceRelatedObjectsQuery({
    variables: source
      ? {
          sourceId: source?.sourceId,
          sourceType: source?.sourceType,
        }
      : undefined,
    skip: !source,
  });

  const lastBackendImprovedRef = useRef<string>('');

  const myId = localStorage.getItem(UserLocStore.id);

  const { data: userLocale } = useUserLocaleQuery({
    variables: myId ? { id: myId } : undefined,
    skip: !myId,
  });

  const defaultLocale = (userLocale?.user?.locale ?? '').toLowerCase();
  const improvedText = Form.useWatch(AiWriterModalFormFieldEnum.ImprovedMessage, form);
  const isImprovedEmpty = !improvedText?.trim();

  const {
    finalTranslation,
    proofReading,
    handleTranslateMessage,
    isPending: isTranslationPending,
    error: translationError,
    reset: resetTranslation,
  } = useAiTranslator({
    cache: translationCache,
  });

  const handleOpenModal = useCallback(
    ({ messageText, sourceId, sourceType, isVisibleToClient, isVisibleToPartner }: HandleOpenModalArgs) => {
      form.setFieldsValue({
        [AiWriterModalFormFieldEnum.SourceMessage]: messageText,
        [AiWriterModalFormFieldEnum.Instructions]: '',
        [AiWriterModalFormFieldEnum.ImprovedMessage]: '',
        [AiWriterModalFormFieldEnum.ProofReading]: '',
      } as Partial<AiWriterModalFormValues>);

      lastBackendImprovedRef.current = '';
      setIsUserEdited(false);
      setLastAction(null);

      setSource({
        sourceId,
        sourceType,
        isVisibleToClient,
        isVisibleToPartner,
      });

      resetTranslation();
      openModal();
    },
    [form, openModal, resetTranslation],
  );

  const handleTranslateClick = useCallback(() => {
    setLastAction(TranslateRequest.REWRITE);

    const text = form.getFieldValue(AiWriterModalFormFieldEnum.SourceMessage) as string;
    const targetLanguage = (form.getFieldValue(AiWriterModalFormFieldEnum.Locale) as string) || UserLocaleEnum.En;
    const instructions = form.getFieldValue(AiWriterModalFormFieldEnum.Instructions) as string | undefined;
    const proofReadingLanguage =
      (form.getFieldValue(AiWriterModalFormFieldEnum.ProofReadingLocale) as string) || defaultLocale;

    if (source) {
      amplitudeLogger?.({
        event: AmplitudeMessengerEvent.AI_WRITER_TRANSLATE,
        attrs: {
          sourceId: source?.sourceId,
          sourceType: source?.sourceType,
          targetLanguage,
          proofReadingLanguage,
        },
      });
    }

    void handleTranslateMessage({
      fieldName: '',
      targetId: String(myId),
      targetType: 'User',
      text,
      targetLanguage,
      instructions,
      proofReadingLanguage,
      translateRequest: TranslateRequest.REWRITE,
    });
  }, [form, defaultLocale, source, handleTranslateMessage, myId]);

  const handleProofreadClick = useCallback(() => {
    setLastAction(TranslateRequest.REFRESH_PROOF_READING);

    const text = form.getFieldValue(AiWriterModalFormFieldEnum.SourceMessage) as string;
    const targetLanguage =
      (form.getFieldValue(AiWriterModalFormFieldEnum.Locale) as string) || defaultLocale || UserLocaleEnum.En;
    const instructions = form.getFieldValue(AiWriterModalFormFieldEnum.Instructions) as string | undefined;
    const proofReadingLanguage =
      (form.getFieldValue(AiWriterModalFormFieldEnum.ProofReadingLocale) as string) || defaultLocale;

    const improved = form.getFieldValue(AiWriterModalFormFieldEnum.ImprovedMessage) as string;

    if (source) {
      amplitudeLogger?.({
        event: AmplitudeMessengerEvent.AI_WRITER_PROOFREAD,
        attrs: {
          sourceId: source?.sourceId,
          sourceType: source?.sourceType,
          targetLanguage,
          proofReadingLanguage,
        },
      });
    }

    void handleTranslateMessage({
      fieldName: '',
      targetId: String(myId),
      targetType: 'User',
      text,
      targetLanguage,
      instructions,
      proofReadingLanguage,
      translateRequest: TranslateRequest.REFRESH_PROOF_READING,
      translation: improved,
    });
  }, [form, defaultLocale, source, handleTranslateMessage, myId]);

  const handleResetImproved = useCallback(() => {
    form.setFieldValue(AiWriterModalFormFieldEnum.ImprovedMessage, lastBackendImprovedRef.current ?? '');
    setIsUserEdited(false);

    if (source) {
      amplitudeLogger?.({
        event: AmplitudeMessengerEvent.AI_WRITER_RESET_IMPROVED,
        attrs: {
          sourceId: source?.sourceId,
          sourceType: source?.sourceType,
        },
      });
    }
  }, [form, source]);

  const handleImprovedChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.nativeEvent.isTrusted) {
      const val = e.target.value;
      setIsUserEdited(val !== lastBackendImprovedRef.current);
    }
  };

  const handleApply = () => {
    const improvedText = (form.getFieldValue(AiWriterModalFormFieldEnum.ImprovedMessage) as string).trim();

    if (source) {
      amplitudeLogger?.({
        event: AmplitudeMessengerEvent.AI_WRITER_APPLY,
        attrs: {
          sourceId: source?.sourceId,
          sourceType: source?.sourceType,
        },
      });
    }

    if (improvedText) {
      sendEvent?.(EMessengerEventBusEvents.SET_INPUT_TEXT, {
        messageText: form.getFieldValue(AiWriterModalFormFieldEnum.ImprovedMessage) as string,
      });
    }

    closeModal();
  };

  const addEventListeners = useCallback(
    (eb: MessengerEventBus) => {
      eb.addEventListener(EMessengerEventBusEvents.OPEN_AI_WRITER_MODAL, handleOpenModal);
    },
    [handleOpenModal],
  );

  const removeEventListeners = useCallback(
    (eb: MessengerEventBus) => {
      eb.removeEventListener(EMessengerEventBusEvents.OPEN_AI_WRITER_MODAL, handleOpenModal);
    },
    [handleOpenModal],
  );

  useEffect(() => {
    if (!modalVisible) return;

    const personLocaleRaw = relatedData?.messengerSourceRelatedObjects?.person?.locale;
    const providerLocaleRaw = relatedData?.messengerSourceRelatedObjects?.provider?.locale;

    const personLocale = getSafeLocale(personLocaleRaw);
    const providerLocale = getSafeLocale(providerLocaleRaw);
    const proofLocale = defaultLocale ? getSafeLocale(defaultLocale) : UserLocaleEnum.En;

    // For client / partner visibility we either:
    // - use related locale when it's available
    // - or fall back to UserLocaleEnum.En while data is still loading / missing
    if (source?.isVisibleToClient) {
      form.setFieldValue(AiWriterModalFormFieldEnum.Locale, personLocale ?? UserLocaleEnum.En);
    } else if (source?.isVisibleToPartner) {
      form.setFieldValue(AiWriterModalFormFieldEnum.Locale, providerLocale ?? UserLocaleEnum.En);
    } else {
      form.setFieldValue(AiWriterModalFormFieldEnum.Locale, UserLocaleEnum.En);
    }

    // Only set proof-reading locale when we actually know the user's default locale
    form.setFieldValue(AiWriterModalFormFieldEnum.ProofReadingLocale, proofLocale);
  }, [modalVisible, defaultLocale, form, source?.isVisibleToClient, source?.isVisibleToPartner, relatedData]);

  useEffect(() => {
    if (!messengerEventBus) return;

    addEventListeners(messengerEventBus);

    return () => removeEventListeners(messengerEventBus);
  }, [addEventListeners, messengerEventBus, removeEventListeners]);

  /**
   * When backend completes a rewrite:
   * - update ImprovedMessage in the form
   * - store the new backend-originated value
   * - mark field as "not user-edited"
   */
  useEffect(() => {
    if (lastAction === TranslateRequest.REWRITE && typeof finalTranslation === 'string') {
      form.setFieldValue(AiWriterModalFormFieldEnum.ImprovedMessage, finalTranslation);
      lastBackendImprovedRef.current = finalTranslation;
      setIsUserEdited(false);
    }
  }, [finalTranslation, lastAction, form]);

  /**
   * When proof-reading result arrives and the panel is open:
   * - update ProofReading field automatically
   */
  useEffect(() => {
    if (isProofOpen && typeof proofReading === 'string') {
      form.setFieldValue(AiWriterModalFormFieldEnum.ProofReading, proofReading);
    }
  }, [form, isProofOpen, proofReading]);

  return {
    form,
    modalVisible,
    isProofOpen,
    isUserEdited,
    isImprovedEmpty,
    lastAction,
    isTranslationPending,
    translationError,
    setIsProofOpen,
    openModal,
    closeModal,
    handleOpenModal,
    handleTranslateClick,
    handleProofreadClick,
    handleResetImproved,
    handleImprovedChange,
    handleApply,
  };
};
